import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  inject,
  input,
  type Signal,
} from '@angular/core';
import { NgxEchartsDirective } from 'ngx-echarts';
import type {
  BarSeriesOption,
  EChartsOption,
  XAXisComponentOption,
} from 'echarts';
import { buildEchartsTheme } from '../echarts-theme';

export interface IEchartsBarDatum {
  category: string;
  values: number[];
  /**
   * Optional per-category bar colour. Set it to highlight one category and dim
   * the rest — e.g. the selected practitioner's bar in full colour with the
   * other seats muted. When omitted the series falls back to the theme palette.
   */
  colour?: string;
}

export enum EchartsBarOrientation {
  Vertical = 'vertical',
  Horizontal = 'horizontal',
}

@Component({
  selector: 'pr-echarts-bar-chart',
  templateUrl: './echarts-bar-chart.component.html',
  styleUrls: ['./echarts-bar-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgxEchartsDirective],
})
export class EchartsBarChartComponent {
  private readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly data = input<IEchartsBarDatum[]>([]);
  readonly seriesLabels = input<string[]>([]);
  readonly orientation = input<EchartsBarOrientation>(
    EchartsBarOrientation.Vertical
  );
  readonly label = input.required<string>();
  readonly height = input<number>(280);
  readonly ariaLabelOverride = input<string>();

  readonly hasData = computed(() => this.data().length > 0);

  readonly ariaLabel = computed(() => {
    const override = this.ariaLabelOverride();
    if (override) {
      return override;
    }
    const items = this.data();
    if (items.length === 0) {
      return this.label();
    }
    const direction =
      this.orientation() === EchartsBarOrientation.Horizontal
        ? EchartsBarOrientation.Horizontal
        : EchartsBarOrientation.Vertical;
    return `${this.label()} ${direction} bar chart, ${items.length} categories`;
  });

  readonly chartOptions: Signal<EChartsOption> = computed(() => {
    const items = this.data();
    const labels = this.seriesLabels();
    if (items.length === 0 || labels.length === 0) {
      return {};
    }

    const theme = buildEchartsTheme(this._elementRef.nativeElement);
    const categories = items.map((item) => item.category);
    const isHorizontal =
      this.orientation() === EchartsBarOrientation.Horizontal;

    const categoryAxis: XAXisComponentOption = {
      type: 'category',
      data: categories,
      axisLabel: { fontSize: 11 },
    };

    const valueAxis: XAXisComponentOption = {
      type: 'value',
    };

    const series: BarSeriesOption[] = labels.map((seriesLabel, index) => ({
      type: 'bar',
      name: seriesLabel,
      data: items.map((item) => {
        const value = item.values[index] ?? 0;
        return item.colour
          ? { value, itemStyle: { color: item.colour } }
          : value;
      }),
      barMaxWidth: 32,
    }));

    return {
      ...theme,
      xAxis: {
        ...(theme.xAxis as Record<string, unknown>),
        ...(isHorizontal ? valueAxis : categoryAxis),
      },
      yAxis: {
        ...(theme.yAxis as Record<string, unknown>),
        ...(isHorizontal ? categoryAxis : valueAxis),
      },
      legend: {
        ...theme.legend,
        show: labels.length > 1,
        top: 0,
      },
      grid: {
        ...theme.grid,
        top: labels.length > 1 ? 40 : 48,
      },
      series,
    };
  });
}
